'use client'

import type { FormEvent, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTrackFormStepViewed } from '@nwwp/segment-analytics'
import { cn, DisclosureSlot, Input, MarketingPageLayout } from '@nwwp/ui-shadcn'
import {
  AGE_OPTIONS,
  ASSET_OPTIONS,
  CONCENTRATION_OPTIONS,
  EMERGENCY_OPTIONS,
  EMPTY_ANSWERS,
  EQUITY_OPTIONS,
  equityFollowUpsComplete,
  hasEquity,
  INCOME_OPTIONS,
  IPO_OPTIONS,
  MATCH_OPTIONS,
  qualifiesForAdvisor,
  SAVINGS_OPTIONS,
  SELLING_OPTIONS,
  shouldShowResults,
  wealthCheckResultPayload,
} from './data'
import type { Answers, SingleOption, Spot } from './data'
import { NumberInput } from '../../controls/NumberInput'
import { ScheduleCallCTA } from '../../controls/ScheduleCallCTA'
import { useTrackingUrl } from '../../hooks/use-tracking-url'
import { buildScheduleRedirectUrl, SCHEDULE_TRACKING_OPTIONS, WEALTH_CHECK_CALENDLY_URL } from '../../utils/ctas'
import { getTrackingParams } from '../../utils/tracking'

export type CreateWealthCheckLeadFn = (
  data: { first_name: string; last_name: string; email: string; phone: string },
  answers: Answers,
  tracking: Record<string, string>
) => Promise<boolean>

const WEALTH_CHECK_SCHEDULE_URL = buildScheduleRedirectUrl(WEALTH_CHECK_CALENDLY_URL, {})

const STEP_LABELS = [
  'Wealth Check intro',
  'Step 1 of 3: About you',
  'Step 2 of 3: Your finances',
  'Step 3 of 3: Equity and IPO',
  'Unlock your Wealth Check results',
  'Your Wealth Check results are ready',
]

// URL-hash fragment per step. Reflected in the address bar so engagement/dropoff
// can be tracked per step, and so browser back/forward move between steps. We use
// the hash (not a query param) deliberately: it never touches the query string, so
// attribution params (UTMs, click IDs) on the landing URL are left untouched.
const STEP_SLUGS = ['intro', 'about-you', 'your-finances', 'equity-ipo', 'unlock', 'results']

/**
 * Multi-step interactive self-assessment. Ported from the marketing-provided
 * static prototype (wealth-check-prototype-A-editorial.html) — intro, about-you,
 * finances, equity/IPO, then a lead-capture gate ("unlock") before the results
 * screen. Same validation and deterministic scoring engine (see ./data.ts).
 *
 * The gate collects name/email/phone to unlock results; on submit it creates a
 * Lead via createWealthCheckLead (Customer.io results email is Phase 2).
 */
export function WealthCheckContent({ createLead }: { createLead: CreateWealthCheckLeadFn }) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>(EMPTY_ANSWERS)
  const [resultsUnlocked, setResultsUnlocked] = useState(false)
  const trackFormStepViewed = useTrackFormStepViewed()
  const liveRef = useRef<HTMLDivElement>(null)
  const screenRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  // Set when a step change originates from a back/forward navigation, so the
  // effect below doesn't push a duplicate history entry for it.
  const skipHistoryPush = useRef(false)

  // Normalize the URL to the intro step on mount (answers always start empty, so a
  // deep-linked later step has no state to restore) and keep browser back/forward
  // in sync with the quiz. Uses the History API directly — no query-string changes,
  // so landing-URL attribution params are preserved.
  useEffect(() => {
    window.history.replaceState({ wealthCheckStep: 0 }, '', `#${STEP_SLUGS[0]}`)
    function onPopState(event: PopStateEvent) {
      const restored = (event.state as { wealthCheckStep?: number } | null)?.wealthCheckStep
      skipHistoryPush.current = true
      setStep(typeof restored === 'number' ? restored : 0)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Reflect the step in the URL hash for engagement/dropoff tracking. Skip the
    // push when the change came from back/forward — the hash already matches.
    if (skipHistoryPush.current) {
      skipHistoryPush.current = false
    } else {
      window.history.pushState({ wealthCheckStep: step }, '', `#${STEP_SLUGS[step] ?? 'intro'}`)
    }

    const stepSlug = STEP_SLUGS[step] ?? 'intro'
    try {
      trackFormStepViewed({
        step: stepSlug,
        type: 'step',
        formName: 'wealth-check',
        ...getTrackingParams(),
      })
    } catch (error) {
      console.warn('trackFormStepViewed failed', { step: stepSlug, error })
    }

    screenRef.current?.focus({ preventScroll: true })
    if (liveRef.current) liveRef.current.textContent = STEP_LABELS[step] ?? ''
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [step, trackFormStepViewed])

  function updateField(key: keyof Omit<Answers, 'equity'>, value: string) {
    setAnswers((prev) => ({ ...prev, [key]: value }))
  }

  function updateEquity(value: string[]) {
    setAnswers((prev) => ({ ...prev, equity: value }))
  }

  function afterFinances() {
    setStep(hasEquity(answers) ? 3 : 4)
  }

  function restart() {
    setAnswers(EMPTY_ANSWERS)
    setResultsUnlocked(false)
    setStep(0)
  }

  const pct = (step / 3) * 100
  const aboutValid = Boolean(answers.age && answers.income && answers.savings)
  const financesValid = Boolean(answers.emergency && answers.assets && answers.match && answers.equity.length > 0)

  const { reflection, spots } = wealthCheckResultPayload(answers, step, resultsUnlocked)
  const showAdvisorCta = qualifiesForAdvisor(answers)
  const showingResults = shouldShowResults(step, resultsUnlocked)

  return (
    <>
      <MarketingPageLayout
        className={cn('max-w-5xl gap-8 py-8 tablet:py-12', showingResults && showAdvisorCta && 'pb-28')}
      >
        {/* Progress bar only during the actual question steps — on the intro it's an
          empty gray track, and on results it's a full green bar; both are just noise. */}
        {step >= 1 && step <= 3 && (
          <div
            role="progressbar"
            aria-label="Wealth Check progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
            className="h-1 w-full overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full bg-green-dark transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        )}
        <div ref={liveRef} className="sr-only" role="status" aria-live="polite" />

        <div ref={screenRef} tabIndex={-1}>
          {step === 0 && <IntroScreen onStart={() => setStep(1)} />}
          {step === 1 && (
            <AboutScreen
              answers={answers}
              onChange={updateField}
              onBack={() => setStep(0)}
              onNext={() => setStep(2)}
              valid={aboutValid}
            />
          )}
          {step === 2 && (
            <FinancesScreen
              answers={answers}
              onChange={updateField}
              onEquityChange={updateEquity}
              onBack={() => setStep(1)}
              onNext={afterFinances}
              valid={financesValid}
            />
          )}
          {step === 3 && (
            <EquityScreen
              answers={answers}
              onChange={updateField}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              nextDisabled={!equityFollowUpsComplete(answers)}
            />
          )}
          {step === 4 && !resultsUnlocked && (
            <LeadGateScreen
              createLead={createLead}
              answers={answers}
              onBack={() => setStep(hasEquity(answers) ? 3 : 2)}
              onSuccess={() => {
                setResultsUnlocked(true)
                // Let the step effect push the entry. replaceState here left results
                // sharing the gate's entry, so the first Back press re-rendered results
                // (step 4 + resultsUnlocked) and appeared to do nothing.
                setStep(5)
              }}
            />
          )}
          {showingResults && (
            <ResultsScreen reflection={reflection} spots={spots} showAdvisorCta={showAdvisorCta} onRestart={restart} />
          )}
        </div>

        {/* Compliance disclosure sits at the bottom of every step, not just results. */}
        <WealthCheckDisclosure />
      </MarketingPageLayout>
      {showingResults && showAdvisorCta && <StickyBookCallAd />}
    </>
  )
}

/**
 * Sticky bottom "book a call" ad shown on the results screen. Only rendered for
 * advisor-eligible users (income ≠ under $100k), matching the results advisor CTA and
 * the disclosure's eligibility statement.
 */
function StickyBookCallAd() {
  const url = useTrackingUrl(WEALTH_CHECK_SCHEDULE_URL, SCHEDULE_TRACKING_OPTIONS)

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card px-4 py-3 shadow-[0_-4px_16px_rgba(20,17,12,0.08)]">
      <a
        href={url}
        className="mx-auto flex max-w-5xl items-center justify-center gap-2 text-body-bold text-foreground hover:text-green-dark"
      >
        Questions? Book a call with an advisor
        <span aria-hidden>→</span>
      </a>
    </div>
  )
}

function WealthCheckDisclosure() {
  return (
    // Strip DisclosureSlot's dashed box so it reads as plain fine print, matching the
    // intro footnote — the section stays for its "Legal disclosures" a11y landmark.
    <DisclosureSlot className="rounded-none border-0 bg-transparent p-0">
      <p>
        This Wealth Check is a free educational self-assessment based only on the ranges you selected. It is general
        information, not personalized financial, investment, tax, or legal advice, and it is not a financial plan or a
        recommendation to buy, sell, or hold any security. NerdWallet Wealth Partners, LLC is a registered investment
        adviser and an affiliate of NerdWallet, Inc. Registration does not imply any particular level of skill or
        training. Advisory services are offered only under a written agreement. The advisor consultation is available
        only to households with $100,000 or more in annual income. See our{' '}
        <a
          href="https://adviserinfo.sec.gov/firm/brochure/337328"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Form ADV 2A
        </a>{' '}
        and{' '}
        <a
          href="https://reports.adviserinfo.sec.gov/crs/crs_337328.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Form CRS
        </a>{' '}
        for details.
      </p>
    </DisclosureSlot>
  )
}

function OptionButton({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-xl border px-4 py-3 text-left text-body-sm-bold transition-colors',
        selected
          ? 'border-green bg-green-surface text-foreground'
          : 'border-border bg-card text-foreground hover:border-green'
      )}
    >
      {label}
    </button>
  )
}

function SingleSelectGroup({
  legend,
  hint,
  options,
  value,
  onChange,
  gridClassName,
}: {
  legend: ReactNode
  hint?: string
  options: SingleOption[]
  value: string | undefined
  onChange: (value: string) => void
  gridClassName?: string
}) {
  return (
    <fieldset className="flex flex-col gap-3 border-0 p-0">
      <legend className="text-body-bold text-foreground">
        {legend}
        {hint ? <span className="ml-1 text-body-xs font-normal text-muted-foreground">{hint}</span> : null}
      </legend>
      <div className={cn('grid grid-cols-2 gap-2', gridClassName)}>
        {options.map((opt) => (
          <OptionButton
            key={opt.value}
            label={opt.label}
            selected={value === opt.value}
            onClick={() => onChange(opt.value)}
          />
        ))}
      </div>
    </fieldset>
  )
}

function EquitySelectGroup({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  function toggle(v: string) {
    if (v === 'none') {
      onChange(value.includes('none') ? [] : ['none'])
      return
    }
    const withoutNone = value.filter((x) => x !== 'none')
    onChange(withoutNone.includes(v) ? withoutNone.filter((x) => x !== v) : [...withoutNone, v])
  }

  return (
    <fieldset className="flex flex-col gap-3 border-0 p-0">
      <legend className="text-body-bold text-foreground">
        Do you have equity compensation?{' '}
        <span className="ml-1 text-body-xs font-normal text-muted-foreground">select any</span>
      </legend>
      <div className="grid grid-cols-2 gap-2 tablet:grid-cols-5">
        {EQUITY_OPTIONS.map((opt) => (
          <OptionButton
            key={opt.value}
            label={opt.label}
            selected={value.includes(opt.value)}
            onClick={() => toggle(opt.value)}
          />
        ))}
      </div>
    </fieldset>
  )
}

function StepNav({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
}: {
  onBack: () => void
  onNext: () => void
  nextLabel: string
  nextDisabled?: boolean
}) {
  return (
    <div className="flex flex-col-reverse items-stretch justify-between gap-3 tablet:flex-row tablet:items-center">
      <button type="button" onClick={onBack} className="text-body-sm-bold text-green-dark underline">
        ← Back
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className="rounded-full bg-foreground px-8 py-4 text-center text-body-sm-bold text-background transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {nextLabel}
      </button>
    </div>
  )
}

function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-5">
        <h1 className="text-title-2xl text-foreground">
          Am I on track for the <em className="text-green-dark">future I want?</em>
        </h1>
        <p className="text-body text-muted-foreground">
          Share a few details about your money and goals, and Wealth Check will point out three things that might be
          holding you back.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-body-xs font-bold text-green-dark uppercase">What you&apos;ll walk away with</p>
        {/* Native cards (not FeatureIconGrid/CalloutCard): needs a full paragraph body plus an inline
            footnote reference on card 2, which those kit compositions don't support. */}
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="text-title-xs mb-3 text-foreground">Your top 3 things worth a look</p>
            <p className="text-body-sm text-muted-foreground">
              Things like an employer match you might be leaving on the table, a thin emergency fund, concentrated stock
              risk, or unused tax-advantaged room — common blind spots we can help you spot.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <p className="text-title-xs mb-3 text-foreground">A call with a financial advisor</p>
            <p className="text-body-sm text-muted-foreground">
              If eligible, talk through your results with a real financial advisor. Just a conversation about
              what&apos;s next.
              <sup>1</sup>
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-start gap-3">
        <button
          type="button"
          onClick={onStart}
          className="rounded-full bg-foreground px-8 py-4 text-body-sm-bold text-background transition-colors hover:bg-charcoal"
        >
          Start my Wealth Check →
        </button>
        <p className="text-body-sm text-muted-foreground">
          Takes about <strong className="font-bold text-green-dark">3 minutes</strong>
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-body-xs font-bold text-green-dark uppercase">Why trust our read</p>
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
          <div className="border-t-2 border-green-dark pt-3">
            <p className="mb-1 text-body-sm-bold text-green-dark">Fee-only fiduciaries</p>
            <p className="text-body-sm text-muted-foreground">
              We&apos;re paid by you, not by commissions — so our read has no product to sell behind it. Partners, not
              salespeople.
            </p>
          </div>
          <div className="border-t-2 border-green-dark pt-3">
            <p className="mb-1 text-body-sm-bold text-green-dark">Credentialed advisors</p>
            <p className="text-body-sm text-muted-foreground">
              Built by CFA® charterholders, CFP® professionals, and CPA professionals with prior experience at Goldman
              Sachs, Fidelity, BNY, and Capital Group.
            </p>
          </div>
          <div className="border-t-2 border-green-dark pt-3">
            <p className="mb-1 text-body-sm-bold text-green-dark">Built for your stage of life</p>
            <p className="text-body-sm text-muted-foreground">
              We specialize in people in their 30s, 40s, and 50s balancing demanding jobs and full personal lives — and
              helping them manage it all.
            </p>
          </div>
        </div>
      </div>

      <p id="wealth-check-footnote" className="text-body-xs text-muted-foreground">
        1. Consumers who make $100,000 or more in household income are eligible for a free meeting with an advisor.
      </p>
    </div>
  )
}

function AboutScreen({
  answers,
  onChange,
  onBack,
  onNext,
  valid,
}: {
  answers: Answers
  onChange: (key: keyof Omit<Answers, 'equity'>, value: string) => void
  onBack: () => void
  onNext: () => void
  valid: boolean
}) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-body-xs font-bold text-green-dark uppercase">Step 1 of 3 · About you</p>
        <h2 className="text-title-xl mt-2 text-foreground">A little about you and your income.</h2>
      </div>

      <SingleSelectGroup
        legend="Roughly how old are you?"
        options={AGE_OPTIONS}
        value={answers.age}
        onChange={(v) => onChange('age', v)}
        gridClassName="tablet:grid-cols-4"
      />
      <SingleSelectGroup
        legend="Household income band"
        options={INCOME_OPTIONS}
        value={answers.income}
        onChange={(v) => onChange('income', v)}
        gridClassName="tablet:grid-cols-5"
      />
      <SingleSelectGroup
        legend="Roughly what share of income do you save?"
        options={SAVINGS_OPTIONS}
        value={answers.savings}
        onChange={(v) => onChange('savings', v)}
        gridClassName="tablet:grid-cols-4"
      />

      <StepNav onBack={onBack} onNext={onNext} nextLabel="Continue →" nextDisabled={!valid} />
    </div>
  )
}

function FinancesScreen({
  answers,
  onChange,
  onEquityChange,
  onBack,
  onNext,
  valid,
}: {
  answers: Answers
  onChange: (key: keyof Omit<Answers, 'equity'>, value: string) => void
  onEquityChange: (value: string[]) => void
  onBack: () => void
  onNext: () => void
  valid: boolean
}) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-body-xs font-bold text-green-dark uppercase">Step 2 of 3 · Your finances</p>
        <h2 className="text-title-xl mt-2 text-foreground">A few quick questions with big consequences.</h2>
      </div>

      <SingleSelectGroup
        legend="Emergency cash on hand"
        options={EMERGENCY_OPTIONS}
        value={answers.emergency}
        onChange={(v) => onChange('emergency', v)}
        gridClassName="tablet:grid-cols-4"
      />
      <SingleSelectGroup
        legend={
          <>
            Roughly how much do you have in invested assets <em>outside</em> retirement accounts?
          </>
        }
        hint="brokerage, cash beyond your buffer"
        options={ASSET_OPTIONS}
        value={answers.assets}
        onChange={(v) => onChange('assets', v)}
        gridClassName="tablet:grid-cols-4"
      />
      <SingleSelectGroup
        legend="401(k) or similar — where do you land?"
        options={MATCH_OPTIONS}
        value={answers.match}
        onChange={(v) => onChange('match', v)}
        // Long labels ("Contributing up to the employer match") get cramped two-up on
        // mobile — stack them full-width there, three-up on tablet.
        gridClassName="grid-cols-1 tablet:grid-cols-3"
      />
      <EquitySelectGroup value={answers.equity} onChange={onEquityChange} />

      <StepNav onBack={onBack} onNext={onNext} nextLabel="Continue →" nextDisabled={!valid} />
    </div>
  )
}

function EquityScreen({
  answers,
  onChange,
  onBack,
  onNext,
  nextDisabled,
}: {
  answers: Answers
  onChange: (key: keyof Omit<Answers, 'equity'>, value: string) => void
  onBack: () => void
  onNext: () => void
  nextDisabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-body-xs font-bold text-green-dark uppercase">Step 3 of 3 · Equity &amp; IPO</p>
        <h2 className="text-title-xl mt-2 text-foreground">A closer look at your equity.</h2>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Where high earners have the most at stake — and often the least clarity.
        </p>
      </div>

      <SingleSelectGroup
        legend="Roughly what share of your net worth is tied to your employer's stock?"
        options={CONCENTRATION_OPTIONS}
        value={answers.concentration}
        onChange={(v) => onChange('concentration', v)}
        gridClassName="tablet:grid-cols-5"
      />
      <SingleSelectGroup
        legend="Is your company pre-IPO or expecting a liquidity event?"
        options={IPO_OPTIONS}
        value={answers.ipo}
        onChange={(v) => onChange('ipo', v)}
        gridClassName="tablet:grid-cols-4"
      />
      <SingleSelectGroup
        legend="Vested shares — selling or just accumulating?"
        options={SELLING_OPTIONS}
        value={answers.selling}
        onChange={(v) => onChange('selling', v)}
        // Three medium-length labels read awkwardly two-up on mobile — stack full-width.
        gridClassName="grid-cols-1 tablet:grid-cols-3"
      />

      <StepNav onBack={onBack} onNext={onNext} nextLabel="See my Wealth Check →" nextDisabled={nextDisabled} />
    </div>
  )
}

/**
 * Lead-capture gate shown before results. Copy is option-1 ("your results are ready")
 * reveal framing; the consent block is the compliance-approved text carried over
 * verbatim from /schedule_form. On submit it creates a Lead via createWealthCheckLead
 * (name/email/phone + the quiz answers, which supply the derived assets range + scoring
 * inputs), then reveals the results.
 *
 * TODO: the Customer.io transactional results email is a fast follow — it needs a CIO
 * transactional template id + an App API key provisioned, plus a send method on
 * @nwwp/customerio-node. The answers + computed results are already persisted to the
 * lead's metadata here, so the email just needs the send wired.
 */
function LeadGateScreen({
  createLead,
  answers,
  onBack,
  onSuccess,
}: {
  createLead: CreateWealthCheckLeadFn
  answers: Answers
  onBack: () => void
  onSuccess: () => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showFieldErrors, setShowFieldErrors] = useState(false)

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const phoneDigits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  const phoneValid = phone.length === 0 ? false : /^[2-9]\d{9}$/.test(phoneDigits)
  const valid = firstName.trim().length > 0 && lastName.trim().length > 0 && emailValid && phoneValid

  const firstNameError = showFieldErrors && firstName.trim().length === 0 ? 'Please enter your first name.' : undefined
  const lastNameError = showFieldErrors && lastName.trim().length === 0 ? 'Please enter your last name.' : undefined
  const emailError = showFieldErrors
    ? email.length === 0
      ? 'Please enter your email address.'
      : emailValid
        ? undefined
        : 'Please enter a valid email address.'
    : undefined
  const phoneError = showFieldErrors
    ? phone.length === 0
      ? 'Please enter your phone number.'
      : phoneValid
        ? undefined
        : 'Please enter a valid phone number.'
    : undefined

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!valid || submitting) {
      if (!valid) setShowFieldErrors(true)
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      const created = await createLead(
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: `+1${phoneDigits}`,
        },
        answers,
        getTrackingParams()
      )
      if (!created) throw new Error('Lead creation failed')
      onSuccess()
    } catch {
      setSubmitError('Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <p className="text-body-xs font-bold text-green-dark uppercase">Almost there</p>
        <h2 className="text-title-xl text-foreground">Your Wealth Check is ready.</h2>
        <p className="text-body text-muted-foreground">
          Tell us where to send it and we&apos;ll reveal the three things most worth a look in your finances right now —
          plus whether you&apos;re eligible for a call with an advisor.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className={cn('flex gap-3', 'flex-col tablet:flex-row')}>
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="wc-first-name" className="text-subtitle font-bold text-neutral-darker uppercase">
              First Name
            </label>
            <Input
              id="wc-first-name"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={firstNameError}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="wc-last-name" className="text-subtitle font-bold text-neutral-darker uppercase">
              Last Name
            </label>
            <Input
              id="wc-last-name"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={lastNameError}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="wc-email" className="text-subtitle font-bold text-neutral-darker uppercase">
            Email Address
          </label>
          <Input
            id="wc-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="wc-phone" className="text-subtitle font-bold text-neutral-darker uppercase">
            Phone Number
          </label>
          <NumberInput
            id="wc-phone"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            mask="phone"
            placeholder="(555) 555-5555"
            autoComplete="tel"
            error={phoneError}
          />
          <p className="text-body-xs text-muted-foreground">
            So an advisor can reach you if you&apos;re eligible for a call. We won&apos;t spam you.
          </p>
        </div>
      </div>

      {/* Consent — compliance-approved copy carried over verbatim from /schedule_form. */}
      <div className="flex flex-col gap-3 text-body-xs text-muted-foreground">
        <p className="text-body-sm text-foreground">
          By clicking &ldquo;Continue&rdquo; below, I agree to the following:
        </p>
        <p>
          <strong className="mb-1 block text-body-xs font-bold text-foreground">Terms and Disclosures</strong>I
          acknowledge and agree to NerdWallet Wealth Partner&apos;s{' '}
          <a
            href="https://www.nerdwallet.com/p/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-dark underline"
          >
            Terms of Use
          </a>{' '}
          and{' '}
          <a
            href="https://www.nerdwallet.com/p/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-dark underline"
          >
            Privacy Policy
          </a>
          , and confirm receipt of NerdWallet Wealth Partner&apos;s{' '}
          <a
            href="https://adviserinfo.sec.gov/firm/brochure/337328"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-dark underline"
          >
            Form ADV Part 2A
          </a>{' '}
          and{' '}
          <a
            href="https://reports.adviserinfo.sec.gov/crs/crs_337328.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-dark underline"
          >
            Form CRS
          </a>
          .
        </p>
        <p>
          <strong className="mb-1 block text-body-xs font-bold text-foreground">
            Consent to Marketing Calls and Texts
          </strong>
          I provide my express written consent to receive marketing calls and text messages at the phone number I
          provided above from NerdWallet Wealth Partners, including their agents and service providers. These
          communications may be sent by using automated technology, including an autodialer, selection system, or
          prerecorded or artificial voice. I provide this consent even if my number is listed on any company-specific,
          state, or federal Do-Not-Call list, or if I have previously opted out of receiving telemarketing calls or text
          messages from NerdWallet. I understand that I may opt out of receiving calls or text messages at any time. I
          certify that the phone number I provided is my own and I am permitted to receive calls and texts at that
          number, and I agree to promptly notify NerdWallet Wealth Partners if I stop using the number entered above.
          Message and data rates may apply. Consent is not required as a condition of using these services.
        </p>
      </div>

      {submitError ? (
        <p role="alert" className="text-body-sm text-red">
          {submitError}
        </p>
      ) : null}

      <div className="flex flex-col-reverse items-stretch justify-between gap-3 tablet:flex-row tablet:items-center">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="text-body-sm-bold text-green-dark underline disabled:opacity-40"
        >
          ← Back
        </button>
        <button
          type="submit"
          disabled={!valid || submitting}
          className="rounded-full bg-foreground px-8 py-4 text-center text-body-sm-bold text-background transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? 'Getting your results…' : 'Continue →'}
        </button>
      </div>
    </form>
  )
}

function ResultsScreen({
  reflection,
  spots,
  showAdvisorCta,
  onRestart,
}: {
  reflection: string
  spots: Spot[]
  showAdvisorCta: boolean
  onRestart: () => void
}) {
  return (
    <div className="flex flex-col gap-8">
      <p className="text-body-xs font-bold text-green-dark uppercase">Your Wealth Check</p>

      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm tablet:p-8">
        <p className="mb-3 text-body-xs font-bold text-green-dark uppercase">What we heard</p>
        <p className="text-title-xs text-foreground italic">&ldquo;{reflection}&rdquo;</p>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-body-xs font-bold text-green-dark uppercase">Top 3 things worth a look</p>
        <div className="flex flex-col gap-3">
          {spots.map((spot) => (
            <div key={spot.h} className="rounded-xl border-l-4 border-green-dark bg-card p-5 shadow-sm">
              <span className="mb-2 inline-block rounded-full bg-green-lighter-2 px-3 py-1 text-body-xs font-bold text-foreground uppercase">
                {spot.tag}
              </span>
              <p className="text-title-xs mb-1 text-foreground">{spot.h}</p>
              <p className="text-body-sm text-muted-foreground">{spot.p}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center">
        <button type="button" onClick={onRestart} className="text-body-sm text-muted-foreground underline">
          ↺ Start over
        </button>
      </div>

      {showAdvisorCta ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
          <p className="text-title-sm mb-2 text-foreground">Want more help? Talk to an advisor.</p>
          <p className="mx-auto mb-5 max-w-[44ch] text-body-sm text-muted-foreground">
            Talk through your results with one of our financial advisors. Just a conversation about what&apos;s next.
          </p>
          {/* cta-dark = filled dark pill; the default `cta` variant is a white pill and
              disappears on this white card (looks like plain text, not a button). */}
          <ScheduleCallCTA
            baseUrl={WEALTH_CHECK_SCHEDULE_URL}
            variant="cta-dark"
            segmentElement="Wealth Check Results CTA"
          >
            Schedule a Call →
          </ScheduleCallCTA>
        </div>
      ) : null}
    </div>
  )
}
